import { useSignIn, useSignUp } from "@clerk/clerk-expo";
import type {
	EmailCodeFactor,
	EmailLinkFactor,
	SignInFirstFactor,
	SignInResource,
	SignUpResource,
} from "@clerk/types";
import { useOnboardingStore } from "@features/onboardings/hooks/useOnboardingStore";
import { useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { Platform } from "react-native";
import {
	type AuthStrategy,
	getAuthStrategy,
	isAuthenticationInvalidError,
	isDuplicateUserError,
	retryDelay,
	tagAsSignUpError,
} from "./helpers";

type ClerkAuthProps = {
	appId: string;
	tenantId: string;
	redirectUrl: string;
	onEmailLinkVerify?: (email: string) => void;
	onEmailCodeSuccess?: () => void;
};

const platform = Platform.OS;

const useClerkAuth = ({
	appId,
	tenantId,
	redirectUrl,
	onEmailLinkVerify,
	onEmailCodeSuccess,
}: ClerkAuthProps) => {
	const [isAuthInProgress, setIsAuthInProgress] = useState(false);

	const { redeemCode } = useLocalSearchParams<{ redeemCode: string }>();

	const { setShowOnboarding } = useOnboardingStore();
	const {
		isLoaded: isSignUpLoaded,
		signUp,
		setActive: setSignUpActive,
	} = useSignUp();
	const {
		isLoaded: isSignInLoaded,
		signIn,
		setActive: setSignInActive,
	} = useSignIn();

	const handleSignUpFlow = async (
		email: string,
		strategy: AuthStrategy,
		passcode: string | undefined,
	): Promise<SignUpResource> => {
		if (!signUp) {
			throw new Error("SignUp is not initialized");
		}

		setShowOnboarding(true);

		const { startEmailLinkFlow } = signUp.createEmailLinkFlow();

		await signUp.create({
			emailAddress: email.trim().toLowerCase(),
			unsafeMetadata: { appId, tenantId },
		});

		if (strategy === "email_code" && passcode) {
			await signUp.prepareEmailAddressVerification({ strategy });
			return signUp.attemptEmailAddressVerification({ code: passcode });
		}

		setIsAuthInProgress(false);

		onEmailLinkVerify?.(email);
		const signUpParams = new URLSearchParams({ mode: "signup", os: platform });
		if (redeemCode) {
			signUpParams.set("redeemCode", redeemCode);
		}
		return startEmailLinkFlow({
			redirectUrl: `${redirectUrl}?${signUpParams.toString()}`,
		});
	};

	const handleSignInFlow = async (
		email: string,
		strategy: AuthStrategy,
		passcode: string | undefined,
	): Promise<SignInResource> => {
		if (!signIn) {
			throw new Error("SignIn is not initialized");
		}

		setShowOnboarding(false);

		const { startEmailLinkFlow } = signIn.createEmailLinkFlow();

		if (strategy === "password" && passcode) {
			return signIn.create({
				identifier: email.trim().toLowerCase(),
				password: passcode,
			});
		}

		const { supportedFirstFactors } = await signIn.create({
			identifier: email,
		});

		const isEmailFactor = (
			factor: SignInFirstFactor,
		): factor is EmailCodeFactor | EmailLinkFactor =>
			factor.strategy === strategy;

		const emailFactor = supportedFirstFactors?.find(isEmailFactor);
		if (!emailFactor) {
			throw new Error("Email address ID not found");
		}

		if (strategy === "email_code" && passcode) {
			await signIn.prepareFirstFactor({
				strategy: "email_code",
				emailAddressId: emailFactor.emailAddressId,
			});
			return signIn.attemptFirstFactor({
				strategy: "email_code",
				code: passcode,
			});
		}

		setIsAuthInProgress(false);

		onEmailLinkVerify?.(email);
		const signInParams = new URLSearchParams({ mode: "signin", os: platform });
		if (redeemCode) {
			signInParams.set("redeemCode", redeemCode);
		}
		return startEmailLinkFlow({
			emailAddressId: emailFactor.emailAddressId,
			redirectUrl: `${redirectUrl}?${signInParams.toString()}`,
		});
	};

	const handleAuthSuccess = async (
		res: SignUpResource | SignInResource,
		strategy: AuthStrategy,
		isSignUp: boolean,
	) => {
		if (res.status === "complete") {
			const setActive = isSignUp ? setSignUpActive : setSignInActive;
			if (!setActive) {
				throw new Error("SetActive is not initialized");
			}

			await setActive({ session: res.createdSessionId });
			if (strategy === "email_code") {
				onEmailCodeSuccess?.();
			}
		}
	};

	const authenticateEmailFlow = async (
		email: string,
		strategy: AuthStrategy,
		passcode: string | undefined,
		isRetry: boolean,
		retry: () => Promise<void>,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: this is a complex flow
	): Promise<void> => {
		try {
			const res = await handleSignUpFlow(email, strategy, passcode);
			await handleAuthSuccess(res, strategy, true);
			return;
		} catch (error) {
			if (!isDuplicateUserError(error)) {
				if (isAuthenticationInvalidError(error) && !isRetry) {
					await retryDelay();
					return retry();
				}
				throw tagAsSignUpError(error);
			}
		}

		try {
			const res = await handleSignInFlow(email, strategy, passcode);
			await handleAuthSuccess(res, strategy, false);
		} catch (signInError) {
			if (isAuthenticationInvalidError(signInError) && !isRetry) {
				await retryDelay();
				return retry();
			}
			throw signInError;
		}
	};

	const authenticate = async (
		email: string,
		passcode?: string,
		isRetry = false,
	) => {
		if (!(isSignUpLoaded && isSignInLoaded)) {
			throw new Error("Clerk is not loaded");
		}

		const strategy = getAuthStrategy(email, passcode);

		setIsAuthInProgress(true);
		try {
			if (strategy === "password") {
				const res = await handleSignInFlow(email, strategy, passcode);
				await handleAuthSuccess(res, strategy, false);
			} else {
				await authenticateEmailFlow(email, strategy, passcode, isRetry, () =>
					authenticate(email, passcode, true),
				);
			}
		} finally {
			setIsAuthInProgress(false);
		}
	};

	return {
		authenticate,
		isAuthInProgress,
		isLoading: !(isSignUpLoaded && isSignInLoaded),
	};
};

export default useClerkAuth;
